import React, { useRef, useMemo } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { signedEditionDndaWorkflow } from "../../../../workflows/workflowDefinitions";
import { signedEditionSadaicWorkflow } from "../../../../workflows/workflowDefinitions";
import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { SignedEditionViewStore } from "../../store/SignedEditionViewStore";
import { useRootStore } from "../../../../store/contexts";
import { EditionView as SnapshotEditionView } from "../snapshot/EditionView";
import { SnapshotExtensionsProvider } from "../../../common/controls";
import {
  SharesPieChart,
  computeSharesFromWorks,
} from "../../../../common/components/SharesPieChart";
import { CombinedWorkflowRoadmap } from "../../../../common/controls";
import { ShowDate } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { FormControl } from "@mui/material";
import { ShowDateTime } from "../../../common/controls";

export type SignedEditionViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  recordId?: string;
  data_Value?: { displayId: string; id: string } | null;
  data_: string;
  dndaStateValue: string;
  sadaicStateValue: string;
  publicationDateValue?: { displayId: string; id: string } | null;
  publicationDate: string | null;
  dndaExpedienteNumberValue?: { displayId: string; id: string } | null;
  dndaExpedienteNumber: string;
  dndaDocumentsRows: CrudRows;
  dndaDocumentsVisibleColumns: GridColumn[];
  dndaDocumentsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  ejemplarPdfFiles: CrudRows;
  ejemplarPdfViewAction?: IMenuAction[];
  dndaComprobantePdfFiles: CrudRows;
  dndaComprobantePdfViewAction?: IMenuAction[];
  blockchainCertificatesRows: CrudRows;
  blockchainCertificatesVisibleColumns: GridColumn[];
  blockchainCertificatesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  dndaUploadLinkValue?: { displayId: string; id: string } | null;
  dndaUploadLink: string;
  dndaUploadKeyReceivedAtValue?: { displayId: string; id: string } | null;
  dndaUploadKeyReceivedAt: Date | null;
  dndaUploadKeyExpiresAtValue?: { displayId: string; id: string } | null;
  dndaUploadKeyExpiresAt: Date | null;
  dndaComprobanteReceivedAtValue?: { displayId: string; id: string } | null;
  dndaComprobanteReceivedAt: Date | null;
  dndaLastComprobantePollAtValue?: { displayId: string; id: string } | null;
  dndaLastComprobantePollAt: Date | null;
  dndaLastOperationalErrorValue?: { displayId: string; id: string } | null;
  dndaLastOperationalError: string;
  sadaicDeliveredAtValue?: { displayId: string; id: string } | null;
  sadaicDeliveredAt: Date | null;
  sadaicLastOperationalErrorValue?: { displayId: string; id: string } | null;
  sadaicLastOperationalError: string;
  createdAt_Value?: { displayId: string; id: string } | null;
  createdAt_: Date | null;
  lastUpdatedAt_Value?: { displayId: string; id: string } | null;
  lastUpdatedAt_: Date | null;
  signedEditionViewStore: SignedEditionViewStore;
};

export const SignedEditionViewForm: React.FC<SignedEditionViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      recordId,
      data_ = "{}",
      dndaStateValue = "",
      sadaicStateValue = "",
      publicationDate = null,
      dndaExpedienteNumber = "",
      dndaDocumentsRows,
      dndaDocumentsVisibleColumns,
      dndaDocumentsSetColumnWiths,
      ejemplarPdfFiles,
      ejemplarPdfViewAction,
      dndaComprobantePdfFiles,
      dndaComprobantePdfViewAction,
      blockchainCertificatesRows,
      blockchainCertificatesVisibleColumns,
      blockchainCertificatesSetColumnWiths,
      dndaUploadLink = "",
      dndaUploadKeyReceivedAt = null,
      dndaUploadKeyExpiresAt = null,
      dndaComprobanteReceivedAt = null,
      dndaLastComprobantePollAt = null,
      dndaLastOperationalError = "",
      sadaicDeliveredAt = null,
      sadaicLastOperationalError = "",
      createdAt_ = null,
      lastUpdatedAt_ = null,
      signedEditionViewStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore, authStore } = rootStore;

      // El cliente del tenant NO opera /automation: los errores internos del
      // circuito (bots, timeouts) no son accionables para él y no deben verse
      // — el roadmap los presenta como "en curso" y el operador los resuelve.
      // ÚNICA excepción accionable por el cliente: falta el comprobante de
      // pago DNDA (detectado sobre el mensaje operacional persistido).
      const isSysAdmin = !!rootStore.authStore.hasRole("SysAdmin");
      const dndaNotice =
        !isSysAdmin && /comprobante/i.test(dndaLastOperationalError || "")
          ? "Falta el comprobante de pago DNDA. Subí un comprobante desde Herramientas/Comprobantes de Pago y continuaremos con el registro."
          : null;

      // Works del snapshot, para el gráfico agregado que se muestra
      // arriba (debajo del estado del workflow). data_ puede llegar como
      // string serializado o ya parseado según el origen.
      const snapshotWorks = useMemo<any[]>(() => {
        try {
          const parsed =
            typeof data_ === "string" ? JSON.parse(data_) : data_;
          return parsed?.works ?? [];
        } catch {
          return [];
        }
      }, [data_]);

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="signedEdition"
              title={langStore.groupSignedEditionSignedEditionLabel}
              collapsible={true}
              expanded={signedEditionViewStore.getCardState("signedEdition")}
              toggleExpand={() =>
                signedEditionViewStore.toggleCardState("signedEdition")
              }
            >
              <FormControl fullWidth>
                <CombinedWorkflowRoadmap
                  concealErrors={!isSysAdmin}
                  primary={{
                    workflow: signedEditionDndaWorkflow,
                    currentState: dndaStateValue,
                    labels: langStore.SignedEditionDndaStateStateLabels,
                    operationalError: dndaLastOperationalError,
                    notice: dndaNotice,
                  }}
                  branches={[
                    {
                      workflow: signedEditionSadaicWorkflow,
                      currentState: sadaicStateValue,
                      labels: langStore.SignedEditionSadaicStateStateLabels,
                      operationalError: sadaicLastOperationalError,
                    },
                  ]}
                />

                {snapshotWorks.length > 0 && (
                  <Box sx={{ px: 2 }}>
                    <SharesPieChart
                      data={computeSharesFromWorks(snapshotWorks)}
                      title={
                        snapshotWorks.length > 1
                          ? "Distribución Promedio Autores y Editoriales"
                          : "Distribución Autores y Editoriales"
                      }
                      subtitle={
                        snapshotWorks.length > 1
                          ? `Promedio de participación neta sobre ${snapshotWorks.length} obras`
                          : "Participación neta de autores y editoriales"
                      }
                      valueLabel={snapshotWorks.length > 1 ? "Promedio" : "Participación"}
                    />
                  </Box>
                )}

                <ShowDate
                  value={publicationDate}
                  label={langStore.SignedEditionPublicationDateLabel}
                  locale={langStore.timeFormat}
                />
                <ShowMultiLine
                  value={dndaExpedienteNumber}
                  label={langStore.SignedEditionDndaExpedienteNumberLabel}
                />
                <ShowItemsCompact
                  rows={dndaDocumentsRows}
                  showLabel={true}
                  label={langStore.SignedEditionDndaDocumentsLabel}
                  columns={dndaDocumentsVisibleColumns}
                  setColumnWidths={dndaDocumentsSetColumnWiths}
                  entityName="AssociatedDocumentSignedEdition"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal(
                      "associatedDocumentSignedEdition",
                      rowId
                    )
                  }
                />
                <ShowFilesCompact
                  rows={ejemplarPdfFiles}
                  showLabel={true}
                  label={langStore.SignedEditionEjemplarPdfLabel}
                  menuActions={ejemplarPdfViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <ShowFilesCompact
                  rows={dndaComprobantePdfFiles}
                  showLabel={true}
                  label={langStore.SignedEditionDndaComprobantePdfLabel}
                  menuActions={dndaComprobantePdfViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <ShowItemsCompact
                  rows={blockchainCertificatesRows}
                  showLabel={true}
                  label={langStore.SignedEditionBlockchainCertificatesLabel}
                  columns={blockchainCertificatesVisibleColumns}
                  setColumnWidths={blockchainCertificatesSetColumnWiths}
                  entityName="SignedEditionBlockchainCert"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal(
                      "signedEditionBlockchainCert",
                      rowId
                    )
                  }
                />
              </FormControl>
            </GroupContainer>

            <SnapshotExtensionsProvider
              extensions={{
                // El gráfico agregado de la Edition se renderiza manualmente
                // arriba (debajo del estado del workflow), no como extensión.
                // El gráfico por obra va arriba de cada MusicalWork (slot top).
                MusicalWork: {
                  top: (data: any) => {
                    if (!data?.authors?.length) return null;
                    return (
                      <Box sx={{ px: 2 }}>
                        <SharesPieChart
                          data={computeSharesFromWorks([data])}
                          title="Distribución Autores y Editoriales"
                          subtitle="Participación neta de autores y editoriales"
                        />
                      </Box>
                    );
                  },
                },
              }}
            >
              <SnapshotEditionView
                snapshotData={data_ as any}
                snapshotEntityName="SignedEdition"
                snapshotRecordId={recordId}
              />
            </SnapshotExtensionsProvider>
            <GroupContainer
              id="dndaTracking"
              title={langStore.groupSignedEditionDndaTrackingLabel}
              description={langStore.groupSignedEditionDndaTrackingDescription}
              collapsible={true}
              expanded={signedEditionViewStore.getCardState("dndaTracking")}
              toggleExpand={() =>
                signedEditionViewStore.toggleCardState("dndaTracking")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={dndaUploadLink}
                  label={langStore.SignedEditionDndaUploadLinkLabel}
                />

                <ShowDateTime
                  value={dndaUploadKeyReceivedAt}
                  label={langStore.SignedEditionDndaUploadKeyReceivedAtLabel}
                />
                <ShowDateTime
                  value={dndaUploadKeyExpiresAt}
                  label={langStore.SignedEditionDndaUploadKeyExpiresAtLabel}
                />
                <ShowDateTime
                  value={dndaComprobanteReceivedAt}
                  label={langStore.SignedEditionDndaComprobanteReceivedAtLabel}
                />
                <ShowDateTime
                  value={dndaLastComprobantePollAt}
                  label={langStore.SignedEditionDndaLastComprobantePollAtLabel}
                />

                {["SysAdmin"].some((role) => authStore.hasRole(role)) && (
                  <ShowMultiLine
                    value={dndaLastOperationalError}
                    label={langStore.SignedEditionDndaLastOperationalErrorLabel}
                  />
                )}
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="sadaicTracking"
              title={langStore.groupSignedEditionSadaicTrackingLabel}
              description={
                langStore.groupSignedEditionSadaicTrackingDescription
              }
              collapsible={true}
              expanded={signedEditionViewStore.getCardState("sadaicTracking")}
              toggleExpand={() =>
                signedEditionViewStore.toggleCardState("sadaicTracking")
              }
            >
              <FormControl fullWidth>
                <ShowDateTime
                  value={sadaicDeliveredAt}
                  label={langStore.SignedEditionSadaicDeliveredAtLabel}
                />

                {["SysAdmin"].some((role) => authStore.hasRole(role)) && (
                  <ShowMultiLine
                    value={sadaicLastOperationalError}
                    label={
                      langStore.SignedEditionSadaicLastOperationalErrorLabel
                    }
                  />
                )}
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="systemDates"
              title={langStore.groupSignedEditionSystemDatesLabel}
              collapsible={true}
              expanded={signedEditionViewStore.getCardState("systemDates")}
              toggleExpand={() =>
                signedEditionViewStore.toggleCardState("systemDates")
              }
            >
              <FormControl fullWidth>
                <ShowDateTime
                  value={createdAt_}
                  label={langStore.SignedEditionCreatedAt_Label}
                />
                <ShowDateTime
                  value={lastUpdatedAt_}
                  label={langStore.SignedEditionLastUpdatedAt_Label}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
